/**
 * Gastronomia como requisito real do pedido.
 *
 * Módulo puro (sem IA, sem Maps, sem créditos): deteta a intenção
 * gastronómica, descreve a cascata de pesquisa determinística e valida se o
 * pedido foi mesmo cumprido antes de marcar a rota como concluída.
 */

import { parseNegativePreferences } from "./negative-preferences";

export type GastronomyKind =
  | "restaurant"
  | "cafe"
  | "bakery"
  | "market"
  | "icecream"
  | "local_food";

export type GastronomyRequirement = {
  /** True → gastronomia é um interesse OBRIGATÓRIO, não decorativo. */
  required: boolean;
  /** Tipos pedidos, por ordem de prioridade. */
  kinds: GastronomyKind[];
  /** Restrições livres detetadas (ex.: "vegetariano", "barato"). */
  constraints: string[];
};

const fold = (value: string) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase();

const KIND_PATTERNS: Array<{ kind: GastronomyKind; re: RegExp }> = [
  { kind: "restaurant", re: /\b(restaurante?s?|restaurants?|ristorant\w*|tascas?|marisqueiras?|bistro\w*|trattoria\w*|osteria\w*|almocar|almoco|jantar|dining|lunch|dinner|comer\s+bem|cenar|dejeuner|abendessen)\b/ },
  { kind: "cafe", re: /\b(cafes?|cafe\s+historico|coffee\s*shops?|cafeteria\w*|caffe\w*|kaffee)\b/ },
  { kind: "bakery", re: /\b(pastelarias?|confeitarias?|padarias?|baker(?:y|ies)|patisserie\w*|boulangerie\w*|panaderia\w*|backerei)\b/ },
  { kind: "market", re: /\b(mercados?|markets?|mercato\w*|marche\w*|feiras?\s+gastronomic\w*|food\s+market\w*)\b/ },
  { kind: "icecream", re: /\b(gelatarias?|geladarias?|sorveterias?|gelateria\w*|ice\s*cream)\b/ },
  { kind: "local_food", re: /\b(gastronomi\w*|gastronomic\w*|comida\s+(?:local|tipica|tradicional)|culinaria|cozinha\s+local|petiscos?|street\s*food|local\s+food|food\s+experience|cuisine|cibo\s+local\w*|essen|typisch\w*\s+essen|degusta\w*|prova\w*\s+de\s+vinhos?|wine\s+tasting)\b/ },
];

const CONSTRAINT_PATTERNS: Array<[RegExp, string]> = [
  [/\b(vegetarian\w*|vegetarian[oa]s?)\b/, "vegetarian"],
  [/\b(vegan\w*|vegan[oa]s?)\b/, "vegan"],
  [/\b(sem\s+gluten|gluten\s*free|celiac\w*)\b/, "gluten_free"],
  [/\b(halal)\b/, "halal"],
  [/\b(kosher|casher)\b/, "kosher"],
  [/\b(barat\w*|economic\w*|cheap|budget|low\s+cost)\b/, "budget"],
  [/\b(luxo|luxury|fine\s*dining|estrela\s+michelin|michelin)\b/, "fine_dining"],
];

export function detectGastronomyRequirement(prompt: string): GastronomyRequirement {
  const p = fold(prompt);
  const { excludedCategories } = parseNegativePreferences(prompt);
  const excluded = new Set(excludedCategories);
  const kinds = KIND_PATTERNS.filter(({ re, kind }) => re.test(p) && !excluded.has(kind as any)).map(({ kind }) => kind);
  const constraints = CONSTRAINT_PATTERNS.filter(([re]) => re.test(p)).map(([, id]) => id);
  return { required: kinds.length > 0, kinds, constraints };
}

/** Categorias de resultado que satisfazem cada tipo pedido. */
const SATISFIED_BY: Record<GastronomyKind, string[]> = {
  restaurant: ["restaurant", "food", "meal_venue"],
  cafe: ["cafe", "coffee_shop", "restaurant", "food"],
  bakery: ["bakery", "pastry", "cafe", "food"],
  market: ["market", "food_market", "food"],
  icecream: ["ice_cream_shop", "icecream", "cafe", "food"],
  local_food: ["restaurant", "cafe", "bakery", "market", "food", "meal_venue", "ice_cream_shop"],
};

export type FulfilmentCheck = {
  fulfilled: boolean;
  /** Tipos pedidos sem qualquer correspondência real na rota. */
  missing: GastronomyKind[];
};

/**
 * Compara os interesses explícitos com as categorias realmente presentes.
 * Impede um "sucesso falso": uma rota só de monumentos quando o utilizador
 * pediu gastronomia não está cumprida.
 */
export function checkGastronomyFulfilment(
  requirement: GastronomyRequirement,
  presentCategories: readonly string[],
): FulfilmentCheck {
  if (!requirement.required) return { fulfilled: true, missing: [] };
  const present = new Set(presentCategories.map((c) => fold(c).replace(/\s+/g, "_")));
  // Basta que UM dos tipos pedidos esteja concretamente representado — não
  // exigimos restaurante quando o pedido era, por exemplo, só pastelaria.
  const satisfiedAny = requirement.kinds.some((kind) =>
    SATISFIED_BY[kind].some((category) => present.has(category)),
  );
  if (satisfiedAny) return { fulfilled: true, missing: [] };
  return { fulfilled: false, missing: [...requirement.kinds] };
}

/**
 * Cascata determinística de pesquisa, económica e com cache:
 * cache perto da rota → categoria → gastronomia local → raio alargado.
 */
export type GastronomySearchStep = {
  stage: "cache" | "category" | "local_cuisine" | "widened";
  radiusMeters: number;
  /** Categorias Places a pesquisar nesta etapa. */
  categories: string[];
};

export function gastronomySearchCascade(
  requirement: GastronomyRequirement,
  baseRadiusMeters: number,
): GastronomySearchStep[] {
  const base = Math.max(400, Math.min(baseRadiusMeters || 900, 4000));
  const categories = requirement.kinds.length
    ? requirement.kinds.flatMap((kind) => SATISFIED_BY[kind]).filter((c, i, a) => a.indexOf(c) === i)
    : ["restaurant", "food"];
  return [
    { stage: "cache", radiusMeters: base, categories },
    { stage: "category", radiusMeters: base, categories },
    { stage: "local_cuisine", radiusMeters: Math.round(base * 1.6), categories },
    { stage: "widened", radiusMeters: Math.min(6000, Math.round(base * 2.5)), categories },
  ];
}

/** Um estabelecimento só é válido se vier de um resultado real e validado. */
export type ValidatedVenue = {
  name?: string | null;
  placeId?: string | null;
  lat?: number | null;
  lng?: number | null;
  address?: string | null;
  mapsUrl?: string | null;
  category?: string | null;
};

export function isValidatedVenue(venue: ValidatedVenue | null | undefined): boolean {
  if (!venue) return false;
  const hasName = typeof venue.name === "string" && venue.name.trim().length >= 2;
  const hasGeo = Number.isFinite(venue.lat) && Number.isFinite(venue.lng);
  const hasId = typeof venue.placeId === "string" && venue.placeId.trim().length > 0;
  return hasName && hasGeo && hasId;
}

/** Copy da recuperação gratuita — nunca "gerar novamente". */
export function gastronomyRecoveryCopy(language = "pt") {
  const pt = {
    message:
      "Não encontrei ainda uma opção gastronómica validada perto desta rota. Queres alargar a pesquisa ou escolher uma zona?",
    widen: "Alargar a pesquisa",
    pickArea: "Escolher uma zona",
    free: "Esta pesquisa extra não gasta créditos.",
  };
  const map: Record<string, typeof pt> = {
    pt,
    en: {
      message:
        "I haven't found a validated food option near this route yet. Do you want to widen the search or pick an area?",
      widen: "Widen the search",
      pickArea: "Pick an area",
      free: "This extra search doesn't use credits.",
    },
    es: {
      message:
        "Todavía no encontré una opción gastronómica validada cerca de esta ruta. ¿Quieres ampliar la búsqueda o elegir una zona?",
      widen: "Ampliar la búsqueda",
      pickArea: "Elegir una zona",
      free: "Esta búsqueda extra no consume créditos.",
    },
    fr: {
      message:
        "Je n'ai pas encore trouvé d'option gastronomique validée près de cet itinéraire. Élargir la recherche ou choisir un quartier ?",
      widen: "Élargir la recherche",
      pickArea: "Choisir un quartier",
      free: "Cette recherche supplémentaire ne consomme pas de crédits.",
    },
    it: {
      message:
        "Non ho ancora trovato un'opzione gastronomica validata vicino a questo itinerario. Vuoi ampliare la ricerca o scegliere una zona?",
      widen: "Amplia la ricerca",
      pickArea: "Scegli una zona",
      free: "Questa ricerca extra non consuma crediti.",
    },
    de: {
      message:
        "Ich habe noch keine geprüfte gastronomische Option in der Nähe dieser Route gefunden. Suche erweitern oder ein Viertel wählen?",
      widen: "Suche erweitern",
      pickArea: "Viertel wählen",
      free: "Diese zusätzliche Suche verbraucht kein Guthaben.",
    },
  };
  return map[(language || "pt").slice(0, 2).toLowerCase()] ?? map.en;
}

export type RouteCompletionVerdict = {
  /** Só true quando todos os interesses obrigatórios têm correspondência. */
  complete: boolean;
  /** Consumir o crédito definitivamente? */
  consumeCredit: boolean;
  /** Recuperação gratuita disponível (sem novo crédito). */
  freeRecovery: boolean;
  missing: GastronomyKind[];
};

/**
 * Verificação final antes de marcar a rota como concluída. Se um interesse
 * obrigatório ficou com zero correspondências, o pedido não está cumprido e o
 * crédito não deve ser finalizado como geração bem-sucedida.
 */
export function verifyRouteCompletion(input: {
  requirement: GastronomyRequirement;
  presentCategories: readonly string[];
}): RouteCompletionVerdict {
  const check = checkGastronomyFulfilment(input.requirement, input.presentCategories);
  return {
    complete: check.fulfilled,
    consumeCredit: check.fulfilled,
    freeRecovery: !check.fulfilled,
    missing: check.missing,
  };
}
